import { useState, useEffect } from 'react';
import Modal from '@/common/Modal';
import EditableTile from '@/common/EditableTile';
import EditableDropdown, { IDropdownOption } from '@/common/EditableDropdown';
import Spinner from '@/common/Spinner';
import { useToast } from '@/context/ToastContext';
import { OrderStaticService, TourBagStaticService } from '@/service';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { PlayerHelper } from '@/library';
import { InternalStatusLookup, OrderAccountForDropdown, PlayerInternalStatuses, PlayerRegionLookup, PlayerStatusLookup, SavePlayerWithBioPayload, SavePlayerWithBioResponse } from '@/api/models';
import styles from './AddPlayerModalWrapper.module.scss';

const initialFormData = {
  firstName: '',
  lastName: '',
  orderAccountForDropdownId: 0,
  playerStatusLookupId: 0,
  playerRegionLookupId: 0,
  playerInternalStatusId: 0,
};

interface IPlayerData {
  firstName: string;
  lastName: string;
  orderAccountForDropdownId: number;
  playerStatusLookupId: number;
  playerRegionLookupId: number;
  playerInternalStatusId: number;
}

function AddPlayerModalWrapper() {
  const { isSyncComplete } = useBackgroundSync();
  const { goToPlayerPage } = useAppNavigation();
  const { showToast } = useToast();
  const isOnline = useOnlineStatus();
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderAccountsForDropdown, setOrderAccountsForDropdown] = useState<OrderAccountForDropdown[]>([]);
  const [playerStatusLookups, setPlayerStatusLookups] = useState<PlayerStatusLookup[]>([]);
  const [playerRegionLookups, setPlayerRegionLookups] = useState<PlayerRegionLookup[]>([]);
  const [playerInternalStatuses, setPlayerInternalStatuses] = useState<PlayerInternalStatuses[]>([]);
  const [internalStatusLookups, setInternalStatusLookups] = useState<InternalStatusLookup[]>([]);
  const [playerInternalStatusOptions, setPlayerInternalStatusOptions] = useState<IDropdownOption[]>([]);
  const [formData, setFormData] = useState<IPlayerData>(initialFormData);
  const [showLoading, setShowLoading] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const [orderAccountsForDropdown, playerStatusLookups, playerRegionLookups, playerInternalStatuses, internalStatusLookups] = await Promise.all([
        OrderStaticService.getOrderAccountsForDropdown(),
        TourBagStaticService.getPlayerStatusLookups(),
        TourBagStaticService.getPlayerRegionLookups(),
        TourBagStaticService.getPlayerInternalStatuses(),
        TourBagStaticService.getInternalStatusLookups(),
      ]);
      setOrderAccountsForDropdown(orderAccountsForDropdown);
      setPlayerStatusLookups(playerStatusLookups);
      setPlayerRegionLookups(playerRegionLookups);
      setPlayerInternalStatuses(playerInternalStatuses);
      setInternalStatusLookups(internalStatusLookups);
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const filtered = playerInternalStatuses.filter((internalStatus) => internalStatus.playerStatusLookupId === Number(formData.playerStatusLookupId));
    const mapped = filtered.map((internalStatus) => {
      const internalStatusLookup = internalStatusLookups.find((statusLookup) => statusLookup.playerInternalStatusLookupId === internalStatus.playerInternalStatusLookupId);
      if (!internalStatusLookup) throw new Error('Failed to find the internalStatusLookup in AddPlayerModalWrapper component');
      return { id: internalStatusLookup.playerInternalStatusLookupId.toString(), label: internalStatusLookup.name };
    });

    setPlayerInternalStatusOptions(mapped);
  }, [formData?.playerStatusLookupId]);

  const handleInputChange = (field: keyof IPlayerData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => Object.values(formData).every((value) => value !== 0 && value !== '');

  const handleSave = async (operation: 'redirect' | 'close') => {
    const isValid = validateForm();
    if (!isValid) {
      setShowValidationError(true);
      return;
    }

    let newPlayer: SavePlayerWithBioResponse;
    try {
      setShowLoading(true);
      const payload: SavePlayerWithBioPayload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        isActive: true,
        defaultTourId: formData.orderAccountForDropdownId,
        defaultTourName: orderAccountsForDropdown.find((orderAccount) => orderAccount.id === Number(formData.orderAccountForDropdownId))?.name || '',
        orderAccountId: formData.orderAccountForDropdownId, // send orderAccountForDropdownId twice to comply with legacy system
        playerBio: {
          playerStatusLookupId: formData.playerStatusLookupId,
          playerStatusLookupName: playerStatusLookups.find((statusLookup) => statusLookup.playerStatusLookupId === Number(formData.playerStatusLookupId))?.name || '',
          playerRegionLookupId: formData.playerRegionLookupId,
          playerRegionLookupName: playerRegionLookups.find((regionLookup) => regionLookup.playerRegionLookupId === Number(formData.playerRegionLookupId))?.name || '',
          playerInternalStatusLookupId: formData.playerInternalStatusId,
          playerInternalStatusLookupName: internalStatusLookups.find((internalStatus) => internalStatus.playerInternalStatusLookupId === Number(formData.playerInternalStatusId))?.name || '',
        },
      };
      newPlayer = await PlayerHelper.savePlayer(payload);
      // TODO: Also add to the player indexed db data

      if (operation === 'redirect') {
        // redirect to newly created player page
        goToPlayerPage({ playerId: newPlayer.playerResults.data.playerId, module: 'Player Info', state: { editMode: true } });
      }

      setShowLoading(false);
      showToast({ message: 'Player Created Successfully', showCheckmark: true });
      closeModal();
    } catch (error) {
      console.error(`Error while trying to save a player with bio: ${error}`);
      setShowError(true);
    }
  };

  const closeModal = () => {
    setShowLoading(false);
    setShowError(false);
    setShowValidationError(false);
    setFormData(initialFormData);
    setIsModalOpen(false);
  };

  if (loading) return null;

  return (
    <div data-testid="tourbag-add-player-modal-wrapper">
      {isSyncComplete && isOnline && (
        <button className={styles.addButton} onClick={() => setIsModalOpen(true)}>
          + Add Player
        </button>
      )}

      <Modal isOpen={isModalOpen} onClose={closeModal} className={styles.modal}>
        <div className={styles.navbar}>
          <span className={styles.title}>Add New Player</span>
          <span className={styles.fieldsRequired}>* All Fields Required</span>
        </div>

        <div className={styles.formContainer}>
          <div className={styles.form}>
            <div className={styles.formRow}>
              <EditableTile label="First Name" value={formData.firstName} onChange={(value) => handleInputChange('firstName', value)} variant="large" alignment="left" className={styles.formField} />

              <EditableTile label="Last Name" value={formData.lastName} onChange={(value) => handleInputChange('lastName', value)} variant="large" alignment="left" className={styles.formField} />
            </div>

            <div className={styles.formRow}>
              <EditableDropdown
                label="Account"
                options={orderAccountsForDropdown.map((orderAccount) => ({ id: orderAccount.id.toString(), label: orderAccount.name }))}
                initialId={formData.orderAccountForDropdownId}
                onChange={(value) => handleInputChange('orderAccountForDropdownId', value)}
                variant="large"
                alignment="left"
                className={styles.formField}
              />

              <EditableDropdown
                label="Player Status"
                initialId={formData.playerStatusLookupId}
                options={playerStatusLookups.map((status) => ({ id: status.playerStatusLookupId.toString(), label: status.name }))}
                onChange={(value) => handleInputChange('playerStatusLookupId', value)}
                variant="large"
                alignment="left"
                className={styles.formField}
              />
            </div>

            <div className={styles.formRow}>
              <EditableDropdown
                label="Region"
                initialId={formData.playerRegionLookupId}
                options={playerRegionLookups.map((region) => ({ id: region.playerRegionLookupId.toString(), label: region.name }))}
                onChange={(value) => handleInputChange('playerRegionLookupId', value)}
                variant="large"
                alignment="left"
                className={styles.formField}
              />

              <EditableDropdown
                label="Internal Status"
                initialId={formData.playerInternalStatusId}
                options={playerInternalStatusOptions}
                onChange={(value) => handleInputChange('playerInternalStatusId', value)}
                variant="large"
                alignment="left"
                className={styles.formField}
              />
            </div>
          </div>
        </div>
        {showLoading && (
          <div className={styles.loading}>
            <span>Saving...</span>
            <Spinner />
          </div>
        )}
        {showValidationError && <span className={styles.error}>Please fill every field to create a Player.</span>}
        {showError && <span className={styles.error}>There was an error while creating the Player, please contact your admin.</span>}

        <div className={styles.buttonContainer}>
          <button type="button" className={`${styles.button} ${styles.cancelButton}`} onClick={closeModal}>
            Cancel
          </button>

          <button type="button" className={`${styles.button} ${styles.quickAddButton}`} onClick={() => handleSave('close')}>
            Quick Add
          </button>

          <button type="button" className={`${styles.button} ${styles.saveContinueButton}`} onClick={() => handleSave('redirect')}>
            Save & Continue
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default AddPlayerModalWrapper;
